import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import apiService from "../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";

// ── Types ──────────────────────────────────────────────────────────────────

type GalleryFileType = "venue_photos" | "pa_system" | "equipment" | "other";

const FILE_TYPE_LABELS: Record<GalleryFileType, string> = {
  venue_photos: "Venue Photos",
  pa_system: "PA System",
  equipment: "Equipment",

  other: "Other",
};

interface AddVenuePhotoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  venueId: number;
}

// ── Component ──────────────────────────────────────────────────────────────

const AddVenuePhotoModal: React.FC<AddVenuePhotoModalProps> = ({
  open,
  onClose,
  onSuccess,
  venueId,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileType, setFileType] = useState<GalleryFileType>("venue_photos");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const handleClose = () => {
    setFiles([]);
    setFileType("venue_photos");
    setUploadProgress({ done: 0, total: 0 });
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) {
      // De-dupe on name+size so re-picking the same folder doesn't duplicate entries
      setFiles((prev) => {
        const existingKeys = new Set(prev.map((f) => `${f.name}_${f.size}`));
        const newOnes = picked.filter(
          (f) => !existingKeys.has(`${f.name}_${f.size}`),
        );
        return [...prev, ...newOnes];
      });
    }
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("file_type", fileType);
        fd.append("venue", String(venueId));
        await apiService.post("/api/venues/document/", fd, false);
        successCount += 1;
      } catch {
        failCount += 1;
      } finally {
        setUploadProgress((prev) => ({ ...prev, done: prev.done + 1 }));
      }
    }

    setUploading(false);

    if (successCount && !failCount) {
      showSuccessToast(
        successCount === 1
          ? "Photo uploaded successfully."
          : `${successCount} photos uploaded successfully.`,
      );
    } else if (successCount && failCount) {
      showErrorToast(
        `${successCount} uploaded, ${failCount} failed. Please retry the failed ones.`,
      );
    } else {
      showErrorToast("Failed to upload photos. Please try again.");
    }

    handleClose();
    if (successCount) onSuccess();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: "1.1rem",
          pb: 1,
        }}
      >
        Add Photos
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
      >
        {/* File type picker */}
        <FormControl fullWidth size="medium" sx={{ mt: 1 }}>
          <InputLabel id="photo-type-label">Photo type</InputLabel>
          <Select
            labelId="photo-type-label"
            value={fileType}
            label="Photo type"
            onChange={(e) => setFileType(e.target.value as GalleryFileType)}
            sx={{
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0,0,0,0.15)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#C29363",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#C29363",
              },
            }}
          >
            {(
              Object.entries(FILE_TYPE_LABELS) as [GalleryFileType, string][]
            ).map(([value, label]) => (
              <MenuItem key={value} value={value} sx={{ py: 1.2 }}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Drop / click zone */}
        <Box>
          <input
            ref={inputRef}
            id="venue-photo-file-input"
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <label
            htmlFor="venue-photo-file-input"
            style={{ width: "100%", display: "block" }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: files.length ? "auto" : 160,
                border: "2px dashed",
                borderColor: files.length ? "#C29363" : "rgba(0,0,0,0.15)",
                borderRadius: 2,
                backgroundColor: files.length
                  ? "rgba(194,147,99,0.03)"
                  : "grey.50",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "#C29363",
                  backgroundColor: "rgba(194,147,99,0.06)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {files.length === 0 && (
                <>
                  <CloudUploadIcon
                    sx={{ fontSize: 44, color: "text.secondary", mb: 1.5 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, WebP supported · select multiple
                  </Typography>
                </>
              )}

              {files.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {files.length} photo{files.length > 1 ? "s" : ""} selected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to add more
                  </Typography>
                </>
              )}
            </Box>
          </label>

          {/* Thumbnail grid of selected files */}
          {files.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                gap: 1,
                mt: 1.5,
              }}
            >
              {files.map((f, index) => (
                <Box
                  key={`${f.name}_${f.size}_${index}`}
                  sx={{
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.1)",
                    aspectRatio: "1 / 1",
                  }}
                >
                  {f.type.startsWith("image/") ? (
                    <Box
                      component="img"
                      src={URL.createObjectURL(f)}
                      alt={f.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.100",
                      }}
                    >
                      <FileIcon sx={{ color: "#C29363" }} />
                    </Box>
                  )}
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      p: "2px",
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(211,47,47,0.85)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          size="medium"
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontWeight: 500,
            borderRadius: 1.5,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!files.length || uploading}
          variant="contained"
          size="medium"
          startIcon={
            uploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            bgcolor: "#C29363",
            fontWeight: 600,
            px: 3,
            borderRadius: 1.5,
            boxShadow: "none",
            "&:hover": { bgcolor: "#a87d4f", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.12)" },
          }}
        >
          {uploading
            ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
            : files.length > 1
              ? `Upload ${files.length} Photos`
              : "Upload Photo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVenuePhotoModal;
